import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, resolveComponent as _resolveComponent, createBlock as _createBlock, createTextVNode as _createTextVNode, withModifiers as _withModifiers, normalizeClass as _normalizeClass, withCtx as _withCtx } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "clip-property"
}
const _hoisted_2 = {
  key: 0,
  class: "struct-field",
  open: ""
}
const _hoisted_3 = ["title"]
const _hoisted_4 = {
  key: 0,
  class: "field-description"
}
const _hoisted_5 = { class: "nested-fields" }
const _hoisted_6 = {
  key: 1,
  class: "list-field"
}
const _hoisted_7 = ["title"]
const _hoisted_8 = {
  key: 0,
  class: "field-description"
}
const _hoisted_9 = {
  key: 1,
  class: "field-description"
}
const _hoisted_10 = {
  key: 2,
  class: "empty-list"
}
const _hoisted_11 = ["open"]
const _hoisted_12 = ["disabled", "aria-label", "onClick"]
const _hoisted_13 = ["disabled", "aria-label", "onClick"]
const _hoisted_14 = ["disabled", "aria-label", "onClick"]
const _hoisted_15 = { class: "nested-fields" }
const _hoisted_16 = ["disabled"]
const _hoisted_17 = ["title"]
const _hoisted_18 = ["checked"]
const _hoisted_19 = {
  key: 3,
  class: "vector-field"
}
const _hoisted_20 = ["title"]
const _hoisted_21 = { class: "vector-axes" }
const _hoisted_22 = ["aria-label", "value", "onInput", "onBlur"]
const _hoisted_23 = ["title"]
const _hoisted_24 = ["value", "min", "max", "step"]
const _hoisted_25 = {
  key: 0,
  value: -1,
  disabled: ""
}
const _hoisted_26 = ["value"]
const _hoisted_27 = ["value"]
const _hoisted_28 = ["value"]

import StudioSelectField from "../../../StudioSelectField.vue";
import EntityPresetValueInput from "../../../EntityPresetEditor/EntityPresetValueInput.vue";
import { computed, ref } from "vue";
import type { ClipPropertyDefinition } from "../../types/DialogueNode";
import { createClipPropertyValues, getClipListLimits, getClipNestedProperties, isClipPropertyVisible, updateClipStructField } from "../../utils/clipProperties";


export default /*@__PURE__*/_defineComponent({
  __name: 'ClipPropertyEditor',
  props: {
    property: {},
    modelValue: {},
    siblingValues: {}
  },
  emits: ["update:modelValue"],
  setup(__props: any, { emit: __emit }) {

const props = __props;
const emit = __emit;

const value = computed(() => props.modelValue ?? props.property.defaultValue);
const fields = computed(() => getClipNestedProperties(props.property, props.siblingValues));
const fieldTitle = computed(() =>
  [props.property.key, props.property.description].filter(Boolean).join("\n"),
);
const structValue = computed(() =>
  createClipPropertyValues(fields.value, value.value),
);
const listValue = computed<unknown[]>(() =>
  Array.isArray(value.value) ? value.value : [],
);
const listLimits = computed(() => getClipListLimits(props.property, props.siblingValues));
const maxItems = computed(() => listLimits.value.max);
const minItems = computed(() => listLimits.value.min);
const vectorParts = computed(() => {
  const parts = String(value.value ?? "0,0,0").split(",");
  return [0, 1, 2].map((index) => parts[index]?.trim() || "0");
});
const vectorDrafts = ref<Record<number, string>>({});
const selectedOptionIndex = computed(() =>
  props.property.options?.findIndex((option) => option.value === value.value) ?? -1,
);

function readNumber(event: Event): number | undefined {
  const input = event.target as HTMLInputElement;
  if (!input.value.trim()) return undefined;
  const number = input.valueAsNumber;
  if (!Number.isFinite(number)) return undefined;
  return Math.min(props.property.max ?? Infinity, Math.max(props.property.min ?? -Infinity, number));
}

function updateNumber(event: Event) {
  const number = readNumber(event);
  if (number !== undefined) emit("update:modelValue", number);
}

function updateText(event: Event) {
  emit("update:modelValue", (event.target as HTMLInputElement).value);
}

function updateBoolean(event: Event) {
  emit("update:modelValue", (event.target as HTMLInputElement).checked);
}

function updateOption(event: Event) {
  const index = Number((event.target as HTMLSelectElement).value);
  const option = props.property.options?.[index];
  if (option) emit("update:modelValue", option.value);
}

function readVectorFloat(text: string): number | undefined {
  if (!/^[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:[eE][+-]?\d+)?$/.test(text)) return undefined;
  const number = Number(text);
  if (!Number.isFinite(Math.fround(number))) return undefined;
  return Math.min(props.property.max ?? Infinity, Math.max(props.property.min ?? -Infinity, number));
}

function updateVector(index: number, event: Event) {
  const input = event.target as HTMLInputElement;
  // 保存输入草稿，使负号、-0、小数点和未完成的指数不被重新渲染吞掉。
  if (!/^[+-]?\d*\.?\d*(?:[eE][+-]?\d*)?$/.test(input.value)) {
    input.value = vectorDrafts.value[index] ?? vectorParts.value[index];
    return;
  }
  vectorDrafts.value[index] = input.value;
  const number = readVectorFloat(input.value);
  if (number === undefined) return;
  const parts = [...vectorParts.value];
  parts[index] = String(number);
  emit("update:modelValue", parts.join(","));
}

function finishVectorInput(index: number, event: Event) {
  // 清空、未完成的负号/指数以及溢出值不写入工程，离开时恢复已保存值。
  const input = event.target as HTMLInputElement;
  const number = readVectorFloat(input.value);
  delete vectorDrafts.value[index];
  input.value = number === undefined ? vectorParts.value[index] : String(number);
}

function updateStructField(key: string, fieldValue: unknown) {
  emit("update:modelValue", updateClipStructField(fields.value, structValue.value, key, fieldValue));
}

function itemFields(item: unknown) {
  return createClipPropertyValues(fields.value, item);
}

function updateListField(index: number, key: string, fieldValue: unknown) {
  emit("update:modelValue", listValue.value.map((item, itemIndex) =>
    itemIndex === index ? { ...itemFields(item), [key]: fieldValue } : item,
  ));
}

function addItem() {
  if (listValue.value.length >= maxItems.value) return;
  emit("update:modelValue", [...listValue.value, createClipPropertyValues(fields.value)]);
}

function removeItem(index: number) {
  if (listValue.value.length <= minItems.value) return;
  emit("update:modelValue", listValue.value.filter((_, itemIndex) => itemIndex !== index));
}

function moveItem(index: number, offset: number) {
  const destination = index + offset;
  if (destination < 0 || destination >= listValue.value.length) return;
  const items = [...listValue.value];
  [items[index], items[destination]] = [items[destination], items[index]];
  emit("update:modelValue", items);
}

return (_ctx: any,_cache: any) => {
  const _component_ClipPropertyEditor = _resolveComponent("ClipPropertyEditor", true)!

  return (_unref(isClipPropertyVisible)(__props.property, __props.siblingValues ?? {}))
    ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
        (__props.property.type === 'struct')
          ? (_openBlock(), _createElementBlock("details", _hoisted_2, [
              _createElementVNode("summary", { title: fieldTitle.value }, [
                _createElementVNode("span", null, _toDisplayString(__props.property.label), 1),
                _createElementVNode("code", null, _toDisplayString(__props.property.key), 1)
              ], 8, _hoisted_3),
              (__props.property.description)
                ? (_openBlock(), _createElementBlock("p", _hoisted_4, _toDisplayString(__props.property.description), 1))
                : _createCommentVNode("", true),
              _createElementVNode("div", _hoisted_5, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(fields.value, (field) => {
                  return (_openBlock(), _createBlock(_component_ClipPropertyEditor, {
                    key: field.key,
                    property: field,
                    "model-value": structValue.value[field.key],
                    "sibling-values": structValue.value,
                    "onUpdate:modelValue": ($event: any) => (updateStructField(field.key, $event))
                  }, null, 8, ["property", "model-value", "sibling-values", "onUpdate:modelValue"]))
                }), 128))
              ])
            ]))
          : (__props.property.type === 'struct-list')
            ? (_openBlock(), _createElementBlock("section", _hoisted_6, [
                _createElementVNode("div", {
                  class: "list-heading",
                  title: fieldTitle.value
                }, [
                  _createElementVNode("span", null, [
                    _createTextVNode(_toDisplayString(__props.property.label) + " ", 1),
                    _createElementVNode("code", null, _toDisplayString(__props.property.key), 1)
                  ]),
                  _createElementVNode("small", null, [
                    _createTextVNode(_toDisplayString(listValue.value.length), 1),
                    (Number.isFinite(maxItems.value))
                      ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                          _createTextVNode(" / " + _toDisplayString(maxItems.value), 1)
                        ], 64))
                      : _createCommentVNode("", true)
                  ])
                ], 8, _hoisted_7),
                (__props.property.description)
                  ? (_openBlock(), _createElementBlock("p", _hoisted_8, _toDisplayString(__props.property.description), 1))
                  : _createCommentVNode("", true),
                (listValue.value.length > maxItems.value)
                  ? (_openBlock(), _createElementBlock("p", _hoisted_9, "当前类型最多允许 " + _toDisplayString(maxItems.value) + " 个条目，请移除多余点位。", 1))
                  : _createCommentVNode("", true),
                (!listValue.value.length)
                  ? (_openBlock(), _createElementBlock("p", _hoisted_10, "暂无条目，点击下方添加。"))
                  : _createCommentVNode("", true),
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(listValue.value, (item, index) => {
                  return (_openBlock(), _createElementBlock("details", {
                    key: index,
                    class: "list-item",
                    open: index === 0
                  }, [
                    _createElementVNode("summary", null, [
                      _createElementVNode("span", null, [
                        _createTextVNode("条目 " + _toDisplayString(index + 1) + " ", 1),
                        _createElementVNode("code", null, "[" + _toDisplayString(index) + "]", 1)
                      ]),
                      _createElementVNode("span", {
                        class: "item-actions",
                        onClick: _cache[0] || (_cache[0] = _withModifiers(() => {}, ["stop"]))
                      }, [
                        _createElementVNode("button", {
                          type: "button",
                          disabled: index === 0,
                          "aria-label": `上移条目 ${index + 1}`,
                          title: "上移",
                          onClick: _withModifiers(($event: any) => (moveItem(index, -1)), ["prevent"])
                        }, "↑", 8, _hoisted_12),
                        _createElementVNode("button", {
                          type: "button",
                          disabled: index === listValue.value.length - 1,
                          "aria-label": `下移条目 ${index + 1}`,
                          title: "下移",
                          onClick: _withModifiers(($event: any) => (moveItem(index, 1)), ["prevent"])
                        }, "↓", 8, _hoisted_13),
                        _createElementVNode("button", {
                          type: "button",
                          disabled: listValue.value.length <= minItems.value,
                          "aria-label": `删除条目 ${index + 1}`,
                          title: "删除",
                          onClick: _withModifiers(($event: any) => (removeItem(index)), ["prevent"])
                        }, "×", 8, _hoisted_14)
                      ])
                    ]),
                    _createElementVNode("div", _hoisted_15, [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(fields.value, (field) => {
                        return (_openBlock(), _createBlock(_component_ClipPropertyEditor, {
                          key: field.key,
                          property: field,
                          "model-value": itemFields(item)[field.key],
                          "sibling-values": itemFields(item),
                          "onUpdate:modelValue": ($event: any) => (updateListField(index, field.key, $event))
                        }, null, 8, ["property", "model-value", "sibling-values", "onUpdate:modelValue"]))
                      }), 128))
                    ])
                  ], 8, _hoisted_11))
                }), 128)),
                _createElementVNode("button", {
                  type: "button",
                  class: "add-item",
                  disabled: listValue.value.length >= maxItems.value,
                  onClick: addItem
                }, _toDisplayString(listValue.value.length >= maxItems.value ? '已达到条目上限' : '＋ 添加条目'), 9, _hoisted_16)
              ]))
            : (__props.property.type === 'boolean')
              ? (_openBlock(), _createElementBlock("label", {
                  key: 2,
                  class: "boolean-field",
                  title: fieldTitle.value
                }, [
                  _createElementVNode("input", {
                    type: "checkbox",
                    checked: value.value === true || value.value === 'True',
                    onChange: updateBoolean
                  }, null, 40, _hoisted_18),
                  _createElementVNode("span", null, [
                    _createTextVNode(_toDisplayString(__props.property.label) + " ", 1),
                    _createElementVNode("code", null, _toDisplayString(__props.property.key), 1)
                  ])
                ], 8, _hoisted_17))
              : (__props.property.type === 'vector3')
                ? (_openBlock(), _createElementBlock("fieldset", _hoisted_19, [
                    _createElementVNode("legend", { title: fieldTitle.value }, [
                      _createTextVNode(_toDisplayString(__props.property.label) + " ", 1),
                      _createElementVNode("code", null, _toDisplayString(__props.property.key), 1)
                    ], 8, _hoisted_20),
                    _createElementVNode("div", _hoisted_21, [
                      (_openBlock(), _createElementBlock(_Fragment, null, _renderList(['X', 'Y', 'Z'], (axis, index) => {
                        return _createElementVNode("label", { key: axis }, [
                          _createElementVNode("span", {
                            class: _normalizeClass(`axis-${axis.toLowerCase()}`)
                          }, _toDisplayString(axis), 3),
                          _createElementVNode("input", {
                            type: "text",
                            inputmode: "decimal",
                            "aria-label": `${__props.property.label} ${axis}`,
                            value: vectorDrafts.value[index] ?? vectorParts.value[index],
                            onInput: ($event: any) => (updateVector(index, $event)),
                            onBlur: ($event: any) => (finishVectorInput(index, $event))
                          }, null, 40, _hoisted_22)
                        ])
                      }), 64))
                    ])
                  ]))
                : (_openBlock(), _createElementBlock("label", {
                    key: 4,
                    class: "scalar-field",
                    title: fieldTitle.value
                  }, [
                    _createElementVNode("span", null, [
                      _createTextVNode(_toDisplayString(__props.property.label) + " ", 1),
                      _createElementVNode("code", null, _toDisplayString(__props.property.key), 1)
                    ]),
                    (__props.property.type === 'number')
                      ? (_openBlock(), _createElementBlock("input", {
                          key: 0,
                          type: "number",
                          value: value.value,
                          min: __props.property.min,
                          max: __props.property.max,
                          step: __props.property.step ?? 'any',
                          onInput: updateNumber
                        }, null, 40, _hoisted_24))
                      : (__props.property.type === 'select')
                        ? (_openBlock(), _createBlock(StudioSelectField, {
                            key: 1,
                            value: selectedOptionIndex.value,
                            onChange: updateOption
                          }, {
                            default: _withCtx(() => [
                              (selectedOptionIndex.value < 0)
                                ? (_openBlock(), _createElementBlock("option", _hoisted_25, _toDisplayString(value.value === '' || value.value == null ? '未设置，请选择' : `未识别：${String(value.value)}`), 1))
                                : _createCommentVNode("", true),
                              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.property.options, (option, index) => {
                                return (_openBlock(), _createElementBlock("option", {
                                  key: index,
                                  value: index
                                }, _toDisplayString(option.label), 9, _hoisted_26))
                              }), 128))
                            ]),
                            _: 1
                          }, 8, ["value"]))
                        : (__props.property.type === 'text')
                          ? (_openBlock(), _createElementBlock("textarea", {
                              key: 2,
                              value: String(value.value ?? ''),
                              rows: "2",
                              onInput: updateText
                            }, null, 40, _hoisted_27))
                          : (__props.property.entityPresetField)
                            ? (_openBlock(), _createBlock(EntityPresetValueInput, {
                                key: 3,
                                field: __props.property.entityPresetField,
                                label: __props.property.label,
                                "model-value": String(value.value ?? ''),
                                "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => (emit('update:modelValue', $event)))
                              }, null, 8, ["field", "label", "model-value"]))
                            : (_openBlock(), _createElementBlock("input", {
                                key: 4,
                                value: String(value.value ?? ''),
                                onInput: updateText
                              }, null, 40, _hoisted_28))
                  ], 8, _hoisted_23))
      ]))
    : _createCommentVNode("", true)
}
}

})